import React, { useState, useEffect, useCallback } from 'react';
import GlassSelect from './GlassSelect';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CompanyInput({ onSubmit, onBatchSubmit, loading, initialData, apiKey }) {
  const [formData, setFormData] = useState({
    company_name: initialData?.name || initialData?.company_name || '',
    // Iter 33b — Tracker → Scout URL carry-through. When the
    // operator clicks "Investigate" from a Tracker row the caller
    // forwards `target_url` (the merged best contact path —
    // official site / FA Full-Time / Pitchero / etc.). If the
    // caller has no explicit `website` we seed the field with
    // `target_url` so the operator sees the discovered URL by
    // default. Manually-entered values always win — see the
    // initialData useEffect below.
    website: initialData?.website || initialData?.target_url || '',
    geography: initialData?.location || initialData?.geography || '',
    vector: '',
    sub_vector: '',
    sector: '',
    custom_sector: '',
  });
  // Iter 33b — preserve the URL-intelligence metadata that arrived
  // alongside `initialData` so we can forward it to /api/scout. Kept
  // out of `formData` so the form fields stay scalar / display-only.
  const [urlIntel, setUrlIntel] = useState({
    target_url: initialData?.target_url || null,
    target_url_source_type: initialData?.target_url_source_type || null,
    target_url_confidence:
      typeof initialData?.target_url_confidence === 'number'
        ? initialData.target_url_confidence
        : null,
    target_url_reason: initialData?.target_url_reason || null,
    best_evidence_url: initialData?.best_evidence_url || null,
    best_evidence_title: initialData?.best_evidence_title || null,
  });
  // Inline validation: 'Other' requires a non-empty custom lens. The
  // string is shown beneath the Custom industry / lens field when the
  // user attempts to submit with Other selected and the field blank.
  const [otherError, setOtherError] = useState('');
  const [batchMode, setBatchMode] = useState(false);

  // Sector catalogue (populated from /api/tracker/sectors — same source of
  // truth the Tracker sector selector uses, so the "PROFILE" chip set and
  // friendlier labels stay aligned automatically).
  const [sectorCatalogue, setSectorCatalogue] = useState({
    sectors: [],
    labels: {},
    profiled_sectors: [],
    aliases: {},
  });
  // Null until first load attempt completes. 'ok' / 'error' afterwards —
  // drives the calm fallback copy below the dropdown when the catalogue
  // can't be fetched (e.g. tenant's plan doesn't include sector_tracker).
  const [sectorLoadState, setSectorLoadState] = useState(null);

  // Sync when initialData changes (pipeline company click). The legacy
  // `industry` text field has been removed from the UI; if the caller
  // passes an `initialData.industry` that exactly matches a known
  // canonical sector, we prefill the new sector dropdown instead. Free-
  // form / non-canonical industry strings are intentionally NOT carried
  // forward — the operator picks a sector or "Other" explicitly.
  useEffect(() => {
    if (initialData) {
      setFormData((prev) => ({
        company_name: initialData.name || initialData.company_name || '',
        // Iter 33b — manually-entered URLs win. Only seed `website`
        // from the Tracker-discovered `target_url` when the caller
        // has no explicit `website` AND the form's current `website`
        // is empty (so a typed value doesn't get clobbered when the
        // pipeline click re-fires this effect).
        website:
          initialData.website
          || (prev.website && prev.website.trim()
            ? prev.website
            : (initialData.target_url || '')),
        geography: initialData.location || initialData.geography || '',
        vector: '',
        sub_vector: '',
        sector: prev.sector || '', // keep current sector pick if the dropdown is already showing
        custom_sector: '',
      }));
      setUrlIntel({
        target_url: initialData.target_url || null,
        target_url_source_type: initialData.target_url_source_type || null,
        target_url_confidence:
          typeof initialData.target_url_confidence === 'number'
            ? initialData.target_url_confidence
            : null,
        target_url_reason: initialData.target_url_reason || null,
        best_evidence_url: initialData.best_evidence_url || null,
        best_evidence_title: initialData.best_evidence_title || null,
      });
      setOtherError('');
      setBatchMode(false);
    }
  }, [initialData]);

  const loadSectors = useCallback(async () => {
    // Note: we deliberately DO NOT gate on `apiKey` — the app's global
    // fetch wrapper (AuthContext) attaches credentials (`X-API-Key` AND
    // JWT cookie) for every backend request, so this works for both
    // auth modes. Gating on `apiKey` used to silently skip the fetch
    // when a user signed in with JWT email/password, which is why the
    // dropdown appeared empty under JWT sessions.
    if (!API_URL) {
      setSectorLoadState('error');
      return;
    }
    try {
      const resp = await fetch(`${API_URL}/api/tracker/sectors`);
      if (!resp.ok) {
        setSectorLoadState('error');
        return;
      }
      const data = await resp.json();
      setSectorCatalogue({
        sectors: Array.isArray(data.sectors) ? data.sectors : [],
        labels: (data && data.labels) || {},
        profiled_sectors: Array.isArray(data.profiled_sectors)
          ? data.profiled_sectors
          : [],
        aliases: (data && data.aliases) || {},
      });
      setSectorLoadState('ok');
    } catch {
      setSectorLoadState('error');
    }
  }, []);

  useEffect(() => {
    loadSectors();
    // Re-run once the caller hands us an API key (covers the legacy
    // localStorage path where the wrapper already carries it, but keeps
    // behaviour identical under either auth mode).
  }, [loadSectors, apiKey]);

  // Resolve an arbitrary industry string to a canonical sector name
  // when (and only when) it is an EXACT case-insensitive match against
  // either the canonical sector list, a display-label override, or a
  // known sector-profile alias. Free-form / non-canonical strings
  // resolve to '' so the operator picks explicitly.
  const resolveCanonicalSector = useCallback(
    (industry) => {
      if (!industry || typeof industry !== 'string') return '';
      const needle = industry.trim().toLowerCase();
      if (!needle) return '';
      const sectors = sectorCatalogue.sectors || [];
      // 1. Exact canonical match
      const direct = sectors.find((s) => s.toLowerCase() === needle);
      if (direct) return direct;
      // 2. Exact display-label match (e.g. "Logistics & Supply Chain / WMS")
      const labels = sectorCatalogue.labels || {};
      for (const [canonical, label] of Object.entries(labels)) {
        if (typeof label === 'string' && label.toLowerCase() === needle) {
          return canonical;
        }
      }
      // 3. Exact alias match (case-insensitive) on profiled-sector aliases
      const aliases = sectorCatalogue.aliases || {};
      for (const [canonical, list] of Object.entries(aliases)) {
        if (Array.isArray(list)) {
          const hit = list.find(
            (a) => typeof a === 'string' && a.toLowerCase() === needle,
          );
          if (hit) return canonical;
        }
      }
      return '';
    },
    [sectorCatalogue],
  );

  // When initialData arrives (Pipeline → Scout flow) and the catalogue
  // is loaded, prefill the sector dropdown from initialData.industry
  // when there's a canonical / alias match. We don't reintroduce the
  // legacy free-text industry field — non-matching values simply leave
  // the sector blank and the operator picks one.
  useEffect(() => {
    if (!initialData || !initialData.industry) return;
    if ((sectorCatalogue.sectors || []).length === 0) return;
    const canonical = resolveCanonicalSector(initialData.industry);
    if (canonical) {
      setFormData((prev) => (
        prev.sector === canonical ? prev : { ...prev, sector: canonical }
      ));
    }
  }, [initialData, sectorCatalogue, resolveCanonicalSector]);

  const [batchText, setBatchText] = useState('');

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSectorChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({
      ...prev,
      sector: value,
      // Reset custom field whenever Other is deselected.
      custom_sector: value === 'Other' ? prev.custom_sector : '',
    }));
    // Clear any prior validation hint as soon as the user changes
    // their selection — calm UX, no lingering red.
    setOtherError('');
  };

  const handleCustomSectorChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, custom_sector: value }));
    if (otherError && value.trim()) setOtherError('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.company_name.trim()) return;

    // Required-when-Other validation. Submit is blocked until the user
    // either picks a different sector or types a custom lens.
    if (formData.sector === 'Other' && !formData.custom_sector.trim()) {
      setOtherError('Please describe the custom industry / lens, or pick a sector.');
      return;
    }

    // Build the payload. Note: the legacy free-text `industry` field
    // is NOT sent from the UI any more — the backend's sector → industry
    // shim handles it (sector or custom_sector becomes industry when
    // industry is omitted). API callers can still POST `industry`
    // directly.
    const payload = {
      company_name: formData.company_name.trim(),
      website: formData.website.trim() || null,
      geography: formData.geography.trim() || null,
      vector: formData.vector.trim() || null,
      sub_vector: formData.sub_vector.trim() || null,
      sector: formData.sector || null,
      custom_sector:
        formData.sector === 'Other' && formData.custom_sector.trim()
          ? formData.custom_sector.trim()
          : null,
      // Iter 33b — forward Tracker-discovered URL intelligence so
      // the Scout `_ensure_company` path persists the best contact
      // path / evidence URL onto the company doc. The backend
      // merge respects manual overrides; we never blindly clobber
      // existing fields. Only emit when the operator hasn't
      // overwritten the website with their own value, otherwise
      // the manual entry should win and we drop the auto-merge
      // metadata to keep the audit trail honest.
      target_url:
        urlIntel.target_url
        && formData.website.trim()
        && formData.website.trim() === (urlIntel.target_url || '').trim()
          ? urlIntel.target_url
          : null,
      target_url_source_type:
        urlIntel.target_url
        && formData.website.trim()
        && formData.website.trim() === (urlIntel.target_url || '').trim()
          ? urlIntel.target_url_source_type
          : null,
      target_url_confidence:
        urlIntel.target_url
        && formData.website.trim()
        && formData.website.trim() === (urlIntel.target_url || '').trim()
          ? urlIntel.target_url_confidence
          : null,
      target_url_reason:
        urlIntel.target_url
        && formData.website.trim()
        && formData.website.trim() === (urlIntel.target_url || '').trim()
          ? urlIntel.target_url_reason
          : null,
      best_evidence_url: urlIntel.best_evidence_url || null,
      best_evidence_title: urlIntel.best_evidence_title || null,
    };
    onSubmit(payload);
  };

  const handleBatchSubmit = () => {
    const lines = batchText.split('\n').map((l) => l.trim()).filter(Boolean);
    if (lines.length === 0) return;
    const companies = lines.map((line) => {
      const parts = line.split(',').map((p) => p.trim());
      return {
        company_name: parts[0],
        industry: parts[1] || null,
        geography: parts[2] || null,
        website: null,
      };
    });
    onBatchSubmit(companies);
  };

  const profiledSet = new Set(sectorCatalogue.profiled_sectors || []);
  const sectorLabelFor = (s) => (sectorCatalogue.labels || {})[s] || s;
  const showCustomSector = formData.sector === 'Other';
  const selectedIsProfiled =
    formData.sector && profiledSet.has(formData.sector);

  return (
    <div className="panel">
      <div
        className="panel-header"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <span>Company Input</span>
        <button
          className={`btn btn-sm ${batchMode ? 'btn-primary' : 'btn-outline'}`}
          onClick={() => setBatchMode(!batchMode)}
          data-testid="batch-mode-toggle"
          type="button"
        >
          {batchMode ? 'Single Mode' : 'Batch Mode'}
        </button>
      </div>
      <div className="panel-body">
        {batchMode ? (
          <div>
            <div className="form-group">
              <label>
                Enter companies (one per line, format: Company Name, Industry,
                Geography)
              </label>
              <textarea
                rows={6}
                value={batchText}
                onChange={(e) => setBatchText(e.target.value)}
                placeholder={
                  'Aviva, Insurance, UK\nShell, Energy, Global\nBarclays, Banking, UK'
                }
                style={{
                  width: '100%',
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: '12px',
                  padding: '10px',
                  resize: 'vertical',
                }}
                data-testid="batch-textarea"
              />
            </div>
            <div
              style={{
                fontSize: '11px',
                color: 'var(--text-secondary)',
                marginBottom: '10px',
              }}
            >
              {batchText.split('\n').filter((l) => l.trim()).length} companies
              detected
            </div>
            <button
              className="btn btn-primary"
              onClick={handleBatchSubmit}
              disabled={loading || !batchText.trim()}
              data-testid="run-batch-scout-button"
              style={{ width: '100%' }}
            >
              {loading ? 'Batch scouting...' : 'Run Batch Scout'}
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="company_name">Company Name *</label>
                <input
                  id="company_name"
                  type="text"
                  value={formData.company_name}
                  onChange={handleChange('company_name')}
                  placeholder="e.g. Aviva, Wipro, Shell"
                  data-testid="company-name-input"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="website">Website</label>
                <input
                  id="website"
                  type="text"
                  value={formData.website}
                  onChange={handleChange('website')}
                  placeholder="e.g. https://aviva.com"
                  data-testid="company-website-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="sector">
                  Industry / Sector{' '}
                  <span
                    style={{
                      fontSize: '10px',
                      color: 'var(--text-secondary)',
                    }}
                  >
                    (optional lens)
                  </span>
                </label>
                <GlassSelect
                  id="sector"
                  className="signal-type-select"
                  style={{ padding: '6px 8px', width: '100%' }}
                  value={formData.sector}
                  onChange={handleSectorChange}
                  data-testid="scout-sector-select"
                >
                  <option value="">— No sector lens —</option>
                  {sectorCatalogue.sectors.map((s) => {
                    const label = sectorLabelFor(s);
                    // NOTE: visible option text MUST stay clean — no
                    // "• PROFILE" suffix. The profiled-sector hint is
                    // rendered as a separate bronze badge below the
                    // select when a profiled sector is chosen. Earlier
                    // iterations baked the word into the <option>
                    // label itself, which (a) read awkwardly in the
                    // collapsed select, (b) leaked into test output
                    // via get_by_text matching, and (c) made certain
                    // profiled sectors look like a separate type.
                    return (
                      <option key={s} value={s}>
                        {label}
                      </option>
                    );
                  })}
                  <option value="Other">Other (custom lens)…</option>
                </GlassSelect>
                {selectedIsProfiled && (
                  <div
                    data-testid="scout-sector-profile-hint"
                    style={{
                      marginTop: '4px',
                      fontSize: '10px',
                      color: 'var(--text-secondary)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    <span
                      data-testid="scout-sector-profile-badge"
                      style={{
                        fontSize: 9,
                        background: 'rgba(186, 151, 123, 0.15)',
                        color: '#7c5a40',
                        padding: '2px 6px',
                        borderRadius: 4,
                        fontWeight: 600,
                        letterSpacing: '0.04em',
                      }}
                    >
                      PROFILE
                    </span>
                    <span>
                      Scout will tag candidates with sector-specific
                      commercial and risk reasons.
                    </span>
                  </div>
                )}
                {sectorLoadState === 'error' && (
                  <div
                    data-testid="scout-sector-load-error"
                    style={{
                      marginTop: '4px',
                      fontSize: '10px',
                      color: 'var(--text-secondary)',
                    }}
                  >
                    Sector options could not be loaded. You can still run
                    Scout with no sector lens or pick "Other" to type a
                    custom industry / lens.
                  </div>
                )}
              </div>

              {showCustomSector && (
                <div className="form-group">
                  <label htmlFor="custom_sector">
                    Custom industry / lens{' '}
                    <span
                      style={{
                        fontSize: '10px',
                        color: 'var(--text-secondary)',
                      }}
                    >
                      (required)
                    </span>
                  </label>
                  <input
                    id="custom_sector"
                    type="text"
                    value={formData.custom_sector}
                    onChange={handleCustomSectorChange}
                    placeholder="e.g. aviation maintenance, private education, local government, hospitality, pharmaceuticals"
                    data-testid="scout-custom-sector-input"
                    aria-invalid={otherError ? 'true' : 'false'}
                    aria-describedby={otherError ? 'scout-custom-sector-error' : undefined}
                  />
                  {otherError && (
                    <div
                      id="scout-custom-sector-error"
                      data-testid="scout-custom-sector-error"
                      style={{
                        marginTop: '4px',
                        fontSize: '10px',
                        color: 'var(--text-secondary)',
                      }}
                    >
                      {otherError}
                    </div>
                  )}
                </div>
              )}

              <div className="form-group">
                <label htmlFor="geography">Geography</label>
                <input
                  id="geography"
                  type="text"
                  value={formData.geography}
                  onChange={handleChange('geography')}
                  placeholder="e.g. UK, US, Global"
                  data-testid="company-geography-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="vector">
                  Vector / Business Area{' '}
                  <span
                    style={{
                      fontSize: '10px',
                      color: 'var(--text-secondary)',
                    }}
                  >
                    (optional)
                  </span>
                </label>
                <input
                  id="vector"
                  type="text"
                  value={formData.vector}
                  onChange={handleChange('vector')}
                  placeholder="e.g. Azure, Broadband, Gaming"
                  data-testid="company-vector-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="sub_vector">
                  Sub-vector / Product{' '}
                  <span
                    style={{
                      fontSize: '10px',
                      color: 'var(--text-secondary)',
                    }}
                  >
                    (optional)
                  </span>
                </label>
                <input
                  id="sub_vector"
                  type="text"
                  value={formData.sub_vector}
                  onChange={handleChange('sub_vector')}
                  placeholder="e.g. Azure DevOps, Xbox, D365"
                  data-testid="company-subvector-input"
                />
              </div>

              <div
                className="form-group full-width"
                style={{ paddingTop: '8px' }}
              >
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={!formData.company_name.trim() || loading}
                  data-testid="run-scout-button"
                  style={{ width: '100%' }}
                >
                  {loading ? 'Scouting signals...' : 'Run Scout'}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
